import React, { useState } from 'react';
import { setupTestUsers, verifyTestUser } from '../utils/setupTestUsers.ts';

const AuthTestPage: React.FC = () => {
  const [setupResults, setSetupResults] = useState<any[]>([]);
  const [verifyResult, setVerifyResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('admin@company.com');
  const [testPassword, setTestPassword] = useState('password123');

  const handleSetupTestUsers = async () => {
    setLoading(true);
    try {
      const results = await setupTestUsers();
      setSetupResults(results);
    } catch (error: any) {
      console.error('Setup error:', error);
      alert(`설정 중 오류 발생: ${error.message}`);
    }
    setLoading(false);
  };

  const handleVerifyUser = async () => {
    setLoading(true);
    try {
      const result = await verifyTestUser(testEmail, testPassword);
      setVerifyResult(result);
    } catch (error: any) {
      console.error('Verify error:', error);
      setVerifyResult({ success: false, error: error.message });
    }
    setLoading(false);
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Firebase 인증 테스트 페이지</h1>
      
      {/* 테스트 사용자 설정 섹션 */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">1. 테스트 사용자 설정</h2>
        <p className="text-gray-600 mb-4">
          Firebase Authentication과 Firestore에 테스트 사용자를 생성합니다.
        </p>
        <button
          onClick={handleSetupTestUsers}
          disabled={loading}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
        >
          {loading ? '처리 중...' : '테스트 사용자 설정'}
        </button>
        
        {setupResults.length > 0 && (
          <div className="mt-4">
            <h3 className="font-medium mb-2">설정 결과:</h3>
            <div className="space-y-2">
              {setupResults.map((result, index) => (
                <div 
                  key={index} 
                  className={`p-2 rounded ${result.success ? 'bg-green-100' : 'bg-red-100'}`}
                >
                  <span className="font-medium">{result.email}</span>
                  <span className="ml-2">- {result.status}</span>
                  {result.error && <span className="ml-2 text-red-600">({result.error})</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 인증 테스트 섹션 */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">2. 사용자 인증 테스트</h2>
        <p className="text-gray-600 mb-4">
          특정 사용자로 로그인을 시도하고 Firestore 데이터를 확인합니다.
        </p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">이메일</label>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">비밀번호</label>
            <input
              type="password"
              value={testPassword}
              onChange={(e) => setTestPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>
          
          <button
            onClick={handleVerifyUser}
            disabled={loading}
            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:opacity-50"
          >
            {loading ? '확인 중...' : '인증 테스트'}
          </button>
        </div>
        
        {verifyResult && (
          <div className="mt-4">
            <h3 className="font-medium mb-2">테스트 결과:</h3>
            <div className={`p-4 rounded ${verifyResult.success ? 'bg-green-100' : 'bg-red-100'}`}>
              {verifyResult.success ? (
                <div>
                  <p className="font-medium text-green-800">✅ 인증 성공!</p>
                  <pre className="mt-2 text-sm bg-white p-2 rounded overflow-auto">
                    {JSON.stringify(verifyResult.data, null, 2)}
                  </pre>
                </div>
              ) : (
                <div>
                  <p className="font-medium text-red-800">❌ 인증 실패</p>
                  <p className="text-red-600">{verifyResult.error}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Firebase 설정 정보 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">3. Firebase 설정 확인사항</h2>
        <div className="space-y-2 text-sm">
          <p>✅ Firebase Console에서 Authentication이 활성화되어 있는지 확인</p>
          <p>✅ Email/Password 인증 방법이 활성화되어 있는지 확인</p>
          <p>✅ Firestore Database가 생성되어 있는지 확인</p>
          <p>✅ Firestore 보안 규칙이 적절히 설정되어 있는지 확인</p>
          <p>✅ Firebase 프로젝트 설정이 올바른지 확인</p>
        </div>
      </div>
    </div>
  );
};

export default AuthTestPage;